import { effacerSession, lireJeton, signalerSessionPerdue } from '../session';

/** Erreur rendue par l'API, avec son code metier (ex. INVALID_CREDENTIALS). */
export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

/** Le serveur n'a pas repondu du tout : hors ligne, DNS, coupure. */
export class ErreurReseau extends Error {
  constructor(message = 'Pas de connexion. Vérifie ton réseau.') {
    super(message);
    this.name = 'ErreurReseau';
  }
}

interface Options {
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corps?: unknown;
  /** `false` pour un appel qui ne doit PAS porter le jeton (la connexion elle-meme). */
  authentifie?: boolean;
  signal?: AbortSignal;
}

interface CorpsErreur {
  error?: { code?: string; message?: string };
}

function jsonOuNull(texte: string): unknown {
  try {
    return JSON.parse(texte) as unknown;
  } catch {
    return null;
  }
}

/**
 * Appelle l'API. Les chemins sont RELATIFS : en production l'application est servie par
 * l'API elle-meme (meme origine), et en developpement Vite renvoie ces prefixes vers le
 * serveur local. Il n'y a donc aucune adresse d'API a configurer nulle part.
 */
export async function appelApi<T>(chemin: string, options: Options = {}): Promise<T> {
  const { methode = 'GET', corps, authentifie = true, signal } = options;
  const jeton = authentifie ? lireJeton() : null;

  const entetes: Record<string, string> = {};
  if (corps !== undefined) entetes['content-type'] = 'application/json';
  if (jeton !== null) entetes.authorization = `Bearer ${jeton}`;

  let reponse: Response;
  try {
    reponse = await fetch(chemin, {
      method: methode,
      headers: entetes,
      body: corps === undefined ? undefined : JSON.stringify(corps),
      signal,
    });
  } catch (cause) {
    // Une annulation volontaire (changement d'ecran) n'est pas une panne de reseau :
    // la relancer telle quelle laisse l'appelant l'ignorer.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ErreurReseau();
  }

  if (reponse.status === 204) return undefined as T;

  const texte = await reponse.text();
  const donnees = texte === '' ? null : jsonOuNull(texte);

  if (!reponse.ok) {
    const erreur = (donnees as CorpsErreur | null)?.error;
    // Jeton expire ou revoque : on nettoie la session, et on le DIT a l'application.
    //
    // TROIS gardes, et les trois sont essentielles.
    //
    // `jeton !== null` : sans elle, un mot de passe errone a la connexion (401 legitime,
    // sans jeton envoye) declencherait une deconnexion et ferait disparaitre le message
    // d'erreur avant que l'utilisateur le lise.
    //
    // `code === 'UNAUTHORIZED'` : le serveur distingue le jeton mort (UNAUTHORIZED) du
    // mot de passe errone DANS LE CORPS (INVALID_CREDENTIALS). Sans cette seconde garde,
    // se tromper de mot de passe actuel en le changeant — ou en supprimant son compte —
    // fermait la session et ejectait l'utilisateur de l'application. Constate par un test
    // le 2026-08-28.
    //
    // `lireJeton() === jeton` : le refus concerne le jeton ENVOYE. Si l'utilisateur s'est
    // reconnecte pendant que la requete voyageait, la session en place est neuve et
    // valide — l'effacer ejecterait quelqu'un qui vient tout juste de se connecter.
    if (
      reponse.status === 401 &&
      jeton !== null &&
      erreur?.code === 'UNAUTHORIZED' &&
      lireJeton() === jeton
    ) {
      effacerSession();
      signalerSessionPerdue();
    }
    throw new ErreurApi(
      reponse.status,
      erreur?.code ?? 'ERREUR',
      erreur?.message ?? 'Une erreur est survenue',
    );
  }

  return donnees as T;
}
